import BackendService from 'BackendService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { fetchExtendedInfo } from '../../utils/ExtendedInfoService';
import { createContentItemRenderer } from '../../utils/ContentItemRenderer';
import {
  ActivityLogFromAPI,
  ComparisonMode,
  ContentPair,
  FullContentDiffViewerProps,
} from './fullContentDiffViewerTypes';

const emptyContentPair = (): ContentPair => ({
  originalContent: null as unknown as ContentManagementInterface,
  newContent: null as unknown as ContentManagementInterface,
});

export function useFullContentDiffViewer({
  fullContent,
  originalContent,
  newContent,
  tone = 'tone1',
  modelId,
  currentVersionId,
}: FullContentDiffViewerProps) {
  const contentToCompare = useMemo(() => {
    if (originalContent && newContent) {
      return { originalContent, newContent };
    }
    if (fullContent) {
      return fullContent;
    }
    return null;
  }, [originalContent, newContent, fullContent]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ActivityLogFromAPI | null>(null);
  const [currentFullContent, setCurrentFullContent] = useState<ContentPair>(
    () => contentToCompare ?? emptyContentPair(),
  );
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('previous');
  const [latestDraftContent, setLatestDraftContent] = useState<ContentManagementInterface | null>(
    null,
  );
  const [publishedContent, setPublishedContent] = useState<ContentManagementInterface | null>(null);
  const [loadingLatestDraft, setLoadingLatestDraft] = useState(false);
  const [loadingPublished, setLoadingPublished] = useState(false);
  const [versions, setVersions] = useState<ActivityLogFromAPI[]>([]);

  const handleVersionSelect = useCallback(
    async (version: ActivityLogFromAPI) => {
      setSelectedVersion(version);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('version', version.id);
      setSearchParams(newSearchParams, { replace: true });

      try {
        const backendService = new BackendService();

        if (version.originalContentPath && version.newContentPath) {
          const loadedOriginalContent = (await fetchExtendedInfo(
            version.originalContentPath,
            backendService,
          )) as ContentManagementInterface;
          const loadedNewContent = (await fetchExtendedInfo(
            version.newContentPath,
            backendService,
          )) as ContentManagementInterface;

          if (loadedOriginalContent && loadedNewContent) {
            setCurrentFullContent({
              originalContent: loadedOriginalContent,
              newContent: loadedNewContent,
            });
          }
        }
      } catch (err) {
        console.error('Error loading version content:', err);
      }
    },
    [searchParams, setSearchParams],
  );

  const loadVersionFromId = useCallback(
    async (versionId: string) => {
      try {
        const backendService = new BackendService();
        const version = await backendService.getActivityLogEntry(versionId);

        if (version && version.originalContentPath && version.newContentPath) {
          await handleVersionSelect({ ...version, id: version.id });
        }
      } catch (err) {
        console.error('Error loading version from URL:', err);
      }
    },
    [handleVersionSelect],
  );

  useEffect(() => {
    if (fullContent) {
      setCurrentFullContent(fullContent);
    }
    setSelectedVersion(null);
    setLatestDraftContent(null);
    setPublishedContent(null);
  }, [fullContent]);

  useEffect(() => {
    const versionId = searchParams.get('version');
    if (versionId && !selectedVersion) {
      loadVersionFromId(versionId);
    }
  }, [searchParams, selectedVersion, loadVersionFromId]);

  useEffect(() => {
    const hasSeenVersionHistory = localStorage.getItem('fides-version-history-tutorial');
    if (!hasSeenVersionHistory) {
      const timer = setTimeout(() => {
        setShowSpeechBubble(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const loadLatestDraftContent = useCallback(async () => {
    if (latestDraftContent || loadingLatestDraft) return;

    try {
      setLoadingLatestDraft(true);
      if (fullContent) {
        setLatestDraftContent(fullContent.newContent);
      }
    } catch (err) {
      console.error('Error loading latest draft content:', err);
    } finally {
      setLoadingLatestDraft(false);
    }
  }, [latestDraftContent, loadingLatestDraft, fullContent]);

  const loadPublishedContent = useCallback(async () => {
    if (publishedContent || loadingPublished) return;

    try {
      setLoadingPublished(true);
      if (fullContent) {
        setPublishedContent(fullContent.originalContent);
      }
    } catch (err) {
      console.error('Error loading published content:', err);
    } finally {
      setLoadingPublished(false);
    }
  }, [publishedContent, loadingPublished, fullContent]);

  useEffect(() => {
    if (comparisonMode === 'latest-draft' && !latestDraftContent) {
      loadLatestDraftContent();
    }
  }, [comparisonMode, latestDraftContent, loadLatestDraftContent]);

  useEffect(() => {
    if (comparisonMode === 'published' && !publishedContent) {
      loadPublishedContent();
    }
  }, [comparisonMode, publishedContent, loadPublishedContent]);

  const handleSpeechBubbleClose = useCallback(() => {
    setShowSpeechBubble(false);
    localStorage.setItem('fides-version-history-tutorial', 'true');
  }, []);

  const handleBackToOriginal = useCallback(() => {
    setSelectedVersion(null);
    if (fullContent) {
      setCurrentFullContent(fullContent);
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('version');
    setSearchParams(newSearchParams, { replace: true });
  }, [fullContent, searchParams, setSearchParams]);

  const isSelectedVersionLatest = useCallback(() => {
    if (!selectedVersion || versions.length === 0) return false;
    return versions[0]?.id === selectedVersion.id;
  }, [selectedVersion, versions]);

  const getCurrentVersionId = useCallback(() => {
    if (selectedVersion) {
      return selectedVersion.id;
    }

    const urlVersionId = searchParams.get('version');
    if (urlVersionId) {
      return urlVersionId;
    }

    return currentVersionId || null;
  }, [selectedVersion, currentVersionId, searchParams]);

  const renderContentItem = useMemo(
    () => createContentItemRenderer(tone, modelId || ''),
    [tone, modelId],
  );

  return {
    contentToCompare,
    currentFullContent,
    sidebarOpen,
    setSidebarOpen,
    selectedVersion,
    showSpeechBubble,
    historyButtonRef,
    comparisonMode,
    setComparisonMode,
    loadingLatestDraft,
    loadingPublished,
    versions,
    setVersions,
    handleVersionSelect,
    handleSpeechBubbleClose,
    handleBackToOriginal,
    isSelectedVersionLatest,
    getCurrentVersionId,
    renderContentItem,
    tone,
  };
}
